import { useEffect, useRef, useCallback } from "react";

/**
 * LinkedIn-style scrollable sticky sidebar hook.
 *
 * Finds the nearest scrolling ancestor automatically (works with custom
 * scroll containers like PersistentFeedLayout, not just window.scrollY).
 *
 * Behavior for sidebars SHORTER than viewport:
 *   → Simple CSS sticky at `topOffset` (no JS needed, CSS handles it).
 *
 * Behavior for sidebars TALLER than viewport:
 *   → Scrolling DOWN: sidebar scrolls naturally, then sticks when its
 *     bottom aligns with the viewport bottom.
 *   → Scrolling UP: sidebar scrolls naturally, then sticks when its
 *     top aligns with the viewport top.
 *   → Direction changes feel natural (no jump).
 *
 * Implementation: dynamically adjusts the CSS `top` property on a
 * `position: sticky` element. No transforms needed.
 *
 * @param {Object} options
 * @param {number} options.topOffset  – px from viewport top when sticking up   (default 16)
 * @param {number} options.bottomOffset – px from viewport bottom when sticking down (default 16)
 * @returns {React.RefObject} ref to attach to the sidebar element
 */
const useLinkedInStickySidebar = ({
  topOffset = 16,
  bottomOffset = 16,
} = {}) => {
  const sidebarRef = useRef(null);
  const lastScrollTop = useRef(0);
  const currentTop = useRef(topOffset);
  const rafId = useRef(null);
  const scrollContainerRef = useRef(null);

  // Find the nearest scrolling ancestor of an element
  const findScrollContainer = useCallback((el) => {
    let node = el.parentElement;
    while (node) {
      const style = getComputedStyle(node);
      const overflowY = style.overflowY;
      if (overflowY === "auto" || overflowY === "scroll") {
        return node;
      }
      node = node.parentElement;
    }
    // Fallback to documentElement (html) or window
    return document.documentElement;
  }, []);

  useEffect(() => {
    const sidebar = sidebarRef.current;
    if (!sidebar) return;

    const scrollContainer = findScrollContainer(sidebar);
    scrollContainerRef.current = scrollContainer;

    // Use the scroll container's viewport height
    const getViewportH = () =>
      scrollContainer === document.documentElement
        ? window.innerHeight
        : scrollContainer.clientHeight;

    const getScrollTop = () =>
      scrollContainer === document.documentElement
        ? window.scrollY || window.pageYOffset
        : scrollContainer.scrollTop;

    // Initialise
    lastScrollTop.current = getScrollTop();
    currentTop.current = topOffset;
    sidebar.style.top = `${topOffset}px`;

    const handleScroll = () => {
      if (rafId.current) return;

      rafId.current = requestAnimationFrame(() => {
        rafId.current = null;
        if (!sidebarRef.current) return;

        const sidebarHeight = sidebar.offsetHeight;
        const viewportH = getViewportH();
        const scrollTop = getScrollTop();
        const scrollDelta = scrollTop - lastScrollTop.current;

        // Short sidebar – CSS sticky at topOffset is all that's needed
        if (sidebarHeight + topOffset + bottomOffset <= viewportH) {
          if (currentTop.current !== topOffset) {
            currentTop.current = topOffset;
            sidebar.style.top = `${topOffset}px`;
          }
          lastScrollTop.current = scrollTop;
          return;
        }

        // Tall sidebar – bidirectional sticky
        const maxTop = topOffset; // stick at top
        const minTop = viewportH - sidebarHeight - bottomOffset; // stick at bottom (negative)

        // Adjust currentTop by the inverse of scroll delta and clamp
        currentTop.current = Math.max(
          minTop,
          Math.min(maxTop, currentTop.current - scrollDelta)
        );

        sidebar.style.top = `${currentTop.current}px`;
        lastScrollTop.current = scrollTop;
      });
    };

    // Bind to the correct scroll target
    const scrollTarget =
      scrollContainer === document.documentElement ? window : scrollContainer;

    scrollTarget.addEventListener("scroll", handleScroll, { passive: true });

    // Reset on resize
    const handleResize = () => {
      currentTop.current = topOffset;
      sidebar.style.top = `${topOffset}px`;
    };
    window.addEventListener("resize", handleResize);

    return () => {
      scrollTarget.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
      if (rafId.current) cancelAnimationFrame(rafId.current);
    };
  }, [topOffset, bottomOffset, findScrollContainer]);

  return sidebarRef;
};

export default useLinkedInStickySidebar;
